import { Injectable } from '@angular/core';
import { environment } from "../../environments/environment";

/**
 * Parametri di campagna (UTM) accettati e inviati al backend per l'evento purchase GA4
 */
export const UTM_KEYS = ['utm_id', 'utm_campaign', 'utm_source', 'utm_medium', 'utm_term', 'utm_content'] as const;

export type UtmParams = Partial<Record<typeof UTM_KEYS[number], string>>;

/**
 * Cattura i parametri UTM dall'URL di atterraggio e li conserva in localStorage.
 * Se l'utente arriva da una campagna successiva il set precedente viene sovrascritto per intero.
 * I valori vengono poi allegati alle chiamate di checkout (PayPal, Stripe, consulto gratuito).
 */
@Injectable({
    providedIn: 'root',
})
export class UtmService {

    static readonly STORAGE_KEY = 'utm_params';
    private static readonly MAX_LENGTH = 255;
    private production = environment.production;

    /**
     * Legge gli UTM dalla query string corrente e, se ce n'e' almeno uno, sostituisce quelli salvati
     */
    captureFromUrl(search: string = window.location.search): UtmParams {
        const found = this.parse(search);
        if (Object.keys(found).length === 0) {
            return this.get();
        }
        this.save(found);
        if (!this.production) {
            console.log('[UTM] captured', found);
        }
        return found;
    }

    /**
     * Restituisce gli UTM salvati (oggetto vuoto se assenti)
     */
    get(): UtmParams {
        try {
            const raw = localStorage.getItem(UtmService.STORAGE_KEY);
            if (!raw) {
                return {};
            }
            const stored = JSON.parse(raw);
            const params: UtmParams = {};
            for (const key of UTM_KEYS) {
                if (typeof stored[key] === 'string' && stored[key] !== '') {
                    params[key] = stored[key];
                }
            }
            return params;
        } catch (e) {
            return {};
        }
    }

    /**
     * Cancella gli UTM salvati
     */
    clear(): void {
        try {
            localStorage.removeItem(UtmService.STORAGE_KEY);
        } catch (e) {
        }
    }

    private save(params: UtmParams): void {
        try {
            localStorage.setItem(UtmService.STORAGE_KEY, JSON.stringify({ ...params, saved_at: new Date().toISOString() }));
        } catch (e) {
            if (!this.production) {
                console.log('[UTM] localStorage non disponibile', e);
            }
        }
    }

    private parse(search: string): UtmParams {
        const params: UtmParams = {};
        let query: URLSearchParams;
        try {
            query = new URLSearchParams(search);
        } catch (e) {
            return params;
        }
        for (const key of UTM_KEYS) {
            const value = (query.get(key) || '').trim();
            if (value !== '') {
                params[key] = value.substring(0, UtmService.MAX_LENGTH);
            }
        }
        return params;
    }
}
